import { useEffect, useRef } from "react";
import { infoScreenStreamUrl } from "@/lib/infoScreenApi";

const SSE_RECONNECT_MS = 5_000;
const CONFIG_RELOAD_DEBOUNCE_MS = 400;

type Options = {
  enabled: boolean;
  onConfigChanged: () => void;
};

/**
 * Live-Updates für Infobildschirm-Anzeigen (SSE), analog zum Gäste-Tablet.
 */
export function useInfoScreenConfigStream(displayCode: string, options: Options) {
  const { enabled, onConfigChanged } = options;
  const onConfigChangedRef = useRef(onConfigChanged);
  onConfigChangedRef.current = onConfigChanged;

  useEffect(() => {
    if (!enabled) return;

    let disposed = false;
    let reloadTimer: number | null = null;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | null = null;

    const clearReload = () => {
      if (reloadTimer != null) {
        window.clearTimeout(reloadTimer);
        reloadTimer = null;
      }
    };

    const scheduleReload = () => {
      if (disposed) return;
      clearReload();
      reloadTimer = window.setTimeout(() => {
        reloadTimer = null;
        onConfigChangedRef.current();
      }, CONFIG_RELOAD_DEBOUNCE_MS);
    };

    const clearReconnect = () => {
      if (reconnectTimer != null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer != null) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, SSE_RECONNECT_MS);
    };

    const connect = () => {
      if (disposed) return;
      eventSource?.close();
      eventSource = null;

      try {
        eventSource = new EventSource(infoScreenStreamUrl(displayCode));
      } catch {
        scheduleReconnect();
        return;
      }

      eventSource.addEventListener("info_screen_config_changed", () => {
        scheduleReload();
      });

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposed = true;
      eventSource?.close();
      clearReconnect();
      clearReload();
    };
  }, [displayCode, enabled]);
}
